/** Pure-army faction combat perks: boards, packs, oaths, and first blows.
 *
 * Movement perks live on `MoveRules`. Everything here is the battle half, gated
 * the same way: the living non-machine stacks of a side must share one faction,
 * and that faction's `FactionDef` names the rule. Mixed armies get nothing.
 */

import type { CreatureDefId, FactionId, ModifierSrcId } from "../ids.js";
import { jSetI32 } from "./journal.js";
import { F_SUMMONED } from "./flags.js";
import { asModifierSrcId } from "../ids.js";
import type { FactionDef } from "../content/registry.js";
import { type ContentRegistry, must } from "../content/registry.js";
import type { ArmySlot, TerrainKind } from "../state.js";
import { TERRAINS } from "../state.js";
import { roundHalfAway, clampInt,
} from "../numeric.js";
import type { Modifier } from "../modifiers.js";
import {
  F_NON_LIVING, F_TWO_HEX, F_UNDEAD, F_WAR_MACHINE, F_WAITED, F_DEFENDING,
  type BattleState, hexNeighbours, isAlive,
} from "./state.js";
import { generateWet } from "./wet.js";
import type { Ctx } from "./apply.js";
import { addMod, damageStack, healStack, rollChanceBp } from "./apply.js";
import { addStack, freeHexFor } from "./summon.js";
import type { RngStreamState } from "../rng/pcg32.js";

export const SRC_RALLY = asModifierSrcId("perk.rally");
export const SRC_CANOPY = asModifierSrcId("perk.canopy");
export const SRC_BOARDING = asModifierSrcId("perk.boarding");
export const SRC_BLOODING = asModifierSrcId("perk.blooding");
export const SRC_OVERCLOCK = asModifierSrcId("perk.overclock");
export const SRC_SHIELD_WALL = asModifierSrcId("perk.shieldWall");

export function battleTerrainOf(terrain: string): TerrainKind | null {
  const t = terrain.toLowerCase();
  for (const k of TERRAINS) {
    if (t === k || t.includes(k)) return k;
  }
  return null;
}

function livingFaction(
  content: ContentRegistry, slots: readonly (ArmySlot | null)[],
): FactionId | null {
  let fac: FactionId | null = null;
  for (const s of slots) {
    if (s === null || s.count <= 0) continue;
    const def = content.creatures.get(s.def);
    if (def === undefined) continue;
    if (fac === null) fac = def.faction;
    else if (fac !== def.faction) return null;
  }
  return fac;
}

export function inputSideFaction(
  content: ContentRegistry, slots: readonly (ArmySlot | null)[],
): FactionDef | null {
  const id = livingFaction(content, slots);
  if (id === null) return null;
  return content.factions.get(id) ?? null;
}

export function sideFaction(
  s: BattleState, content: ContentRegistry, side: 0 | 1,
): FactionDef | null {
  const st = s.stacks;
  let fac: FactionId | null = null;
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i) || st.side[i] !== side) continue;
    if ((st.flags[i]! & F_WAR_MACHINE) !== 0) continue;
    const def = content.creatures.get(st.def[i]!);
    if (def === undefined) continue;
    if (fac === null) fac = def.faction;
    else if (fac !== def.faction) return null;
  }
  if (fac === null) return null;
  return content.factions.get(fac) ?? null;
}

export function hasModFrom(s: BattleState, slot: number, source: ModifierSrcId): boolean {
  for (const m of s.stacks.mods[slot]!) if (m.source === source) return true;
  return false;
}

function mechanical(flags: number): boolean {
  return (flags & (F_NON_LIVING | F_WAR_MACHINE)) !== 0;
}

/** Connected NON_LIVING pack containing `slot` has size >= `need`. */
export function latticePack(s: BattleState, slot: number, need: number): boolean {
  if (!isAlive(s.stacks, slot) || (s.stacks.flags[slot]! & F_NON_LIVING) === 0) return false;
  const side = s.stacks.side[slot]!;
  const n = s.stacks.n;
  const seen = new Uint8Array(n);
  const q: number[] = [slot];
  seen[slot] = 1;
  let size = 0;
  const hexOf: number[] = [];
  const nb: number[] = [0, 0, 0, 0, 0, 0];
  for (let i = 0; i < n; i++) {
    if (!isAlive(s.stacks, i) || s.stacks.side[i] !== side) continue;
    if ((s.stacks.flags[i]! & F_NON_LIVING) === 0) continue;
    hexOf[i] = s.stacks.hex[i]!;
  }
  while (q.length > 0) {
    const cur = q.pop()!;
    size += 1;
    if (size >= need) return true;
    const hn = hexNeighbours(s.stacks.hex[cur]!, nb);
    for (let k = 0; k < hn; k++) {
      const h = nb[k]!;
      for (let i = 0; i < n; i++) {
        if (seen[i] === 1) continue;
        if (hexOf[i] !== h) continue;
        seen[i] = 1;
        q.push(i);
      }
    }
  }
  return false;
}

export function attunementActive(s: BattleState, content: ContentRegistry, side: 0 | 1): boolean {
  const f = sideFaction(s, content, side);
  const groups = f?.attunement?.groups;
  if (groups === undefined) return false;
  const present = new Set<string>();
  const st = s.stacks;
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i) || st.side[i] !== side) continue;
    present.add(st.def[i]! as string);
  }
  for (const group of groups) {
    let hit = false;
    for (const id of group) if (present.has(id as string)) { hit = true; break; }
    if (!hit) return false;
  }
  return true;
}

export function hexMarked(mask: Uint8Array | undefined, hex: number): boolean {
  if (mask === undefined || hex < 0 || hex >= mask.length) return false;
  return mask[hex] !== 0;
}

export function isWaterBattle(s: BattleState): boolean {
  const t = battleTerrainOf(s.terrain);
  return t === "water";
}

export function canopyHalves(
  s: BattleState, content: ContentRegistry, defender: number,
): boolean {
  const f = sideFaction(s, content, s.stacks.side[defender]! as 0 | 1);
  if (f?.canopy === undefined) return false;
  const t = battleTerrainOf(s.terrain);
  if (t !== f.canopy.terrain) return false;
  return !hasModFrom(s, defender, SRC_CANOPY);
}

export function boardingIgnoreBp(
  s: BattleState, content: ContentRegistry, attacker: number,
): number {
  if (!isWaterBattle(s)) return 0;
  if (hasModFrom(s, attacker, SRC_BOARDING)) return 0;
  const f = sideFaction(s, content, s.stacks.side[attacker]! as 0 | 1);
  return f?.boarding?.defIgnoreBp ?? 0;
}

export function cindersBonus(s: BattleState, content: ContentRegistry, attacker: number): boolean {
  const f = sideFaction(s, content, s.stacks.side[attacker]! as 0 | 1);
  if (f?.cinders === undefined) return false;
  return hexMarked(s.fire, s.stacks.hex[attacker]!);
}

export function miasmaPenalty(s: BattleState, content: ContentRegistry, slot: number): boolean {
  const side = s.stacks.side[slot]! as 0 | 1;
  const self = sideFaction(s, content, side);
  if (self?.miasma !== undefined) return false;
  return hexMarked(s.poison, s.stacks.hex[slot]!);
}

/**
 * Rally: a fallen stack lifts the morale of every survivor on its side, for
 * the rest of the battle. Written to the morale COLUMN the way Terrify is,
 * clamped at +3 at the point of writing, and never for the mindless - the
 * undead and the machines roll no morale to lift. Asked of the dead stack's
 * faction, so a mixed army does not rally and neither does a war machine.
 */
export function rallyOnDeath(c: Ctx, content: ContentRegistry, fallen: number): void {
  const s = c.s;
  const st = s.stacks;
  if (isAlive(st, fallen) || (st.flags[fallen]! & (F_SUMMONED | F_WAR_MACHINE)) !== 0) return;
  const side = st.side[fallen]! as 0 | 1;
  const f = sideFaction(s, content, side);
  if (f?.rally === undefined) return;
  for (let i = 0; i < st.n; i++) {
    if (i === fallen || !isAlive(st, i) || st.side[i] !== side) continue;
    if ((st.flags[i]! & (F_UNDEAD | F_WAR_MACHINE)) !== 0) continue;
    jSetI32(c.j, st.morale, i, clampInt(st.morale[i]! + f.rally.morale, -3, 3));
  }
  addMod(c, fallen, {
    source: SRC_RALLY, stat: "morale", op: "add", value: 0, expires: { kind: "battle" },
  });
}

export function paintPerkHexes(
  terrain: string,
  attacker: readonly (ArmySlot | null)[],
  defender: readonly (ArmySlot | null)[],
  content: ContentRegistry,
  rng: RngStreamState,
): { fire?: Uint8Array; poison?: Uint8Array; rng: RngStreamState } {
  const t = battleTerrainOf(terrain);
  let st = rng;
  let fire: Uint8Array | undefined;
  let poison: Uint8Array | undefined;
  for (const slots of [attacker, defender]) {
    const f = inputSideFaction(content, slots);
    if (f?.cinders !== undefined && t !== null && f.cinders.terrains.includes(t) && fire === undefined) {
      const [mask, next] = generateWet("swamp", st);
      st = next;
      fire = mask;
    }
    if (f?.miasma !== undefined && t === f.miasma.terrain && poison === undefined) {
      const [mask, next] = generateWet("swamp", st);
      st = next;
      poison = mask;
    }
  }
  return {
    rng: st,
    ...(fire !== undefined ? { fire } : {}),
    ...(poison !== undefined ? { poison } : {}),
  };
}

export function startModsForStack(
  flags: number, damageMax: number, sidePerk: FactionDef | null,
): readonly Modifier[] {
  const out: Modifier[] = [];
  if (sidePerk?.overclock !== undefined && mechanical(flags)) {
    out.push({
      source: SRC_OVERCLOCK, stat: "speed", op: "add", value: sidePerk.overclock.speed,
      expires: { kind: "battle" },
    });
  }
  return out;
}

/** Shield wall must run BEFORE round-scoped Wait/Defend flags are cleared. */
export function applyShieldWall(c: Ctx, content: ContentRegistry): void {
  const s = c.s;
  const st = s.stacks;
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i)) continue;
    const f = sideFaction(s, content, st.side[i]! as 0 | 1);
    if (f?.shieldWall === undefined) continue;
    if ((st.flags[i]! & (F_WAITED | F_DEFENDING)) === 0) continue;
    addMod(c, i, {
      source: SRC_SHIELD_WALL, stat: "defense", op: "add", value: f.shieldWall.defense,
      expires: { kind: "round", rounds: 1 },
    });
  }
}

export function perkRoundStart(c: Ctx, content: ContentRegistry): void {
  const s = c.s;
  const st = s.stacks;
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i)) continue;
    const side = st.side[i]! as 0 | 1;
    const f = sideFaction(s, content, side);
    if (f?.cinders !== undefined && hexMarked(s.fire, st.hex[i]!)) {
      // Own side is at home in the fire. Everyone else burns.
    } else if (hexMarked(s.fire, st.hex[i]!)) {
      const pool = (st.count[i]! - 1) * st.maxHp[i]! + st.topHp[i]!;
      const dmg = roundHalfAway((pool * 5) / 100);
      if (dmg > 0) damageStack(c, i, dmg);
    }
    if (f?.lattice !== undefined && latticePack(s, i, f.lattice.pack)) {
      healStack(c, i, f.lattice.heal, st.count[i]!);
    }
    if (f?.overclock !== undefined && mechanical(st.flags[i]!)) {
      healStack(c, i, f.overclock.heal, st.count[i]!);
    }
  }
}

export function onPerkKill(
  c: Ctx, content: ContentRegistry, attacker: number, target: number, killed: number,
): void {
  if (killed <= 0) return;
  const s = c.s;
  const st = s.stacks;
  const side = st.side[attacker]! as 0 | 1;
  const f = sideFaction(s, content, side);
  if (f === null) return;

  if (f.blooding !== undefined && !sideHasSource(s, side, SRC_BLOODING)) {
    addMod(c, attacker, {
      source: SRC_BLOODING, stat: "attack", op: "add", value: f.blooding.attack,
      expires: { kind: "battle" },
    });
  }

  if (f.wake === undefined) return;
  if ((st.flags[target]! & (F_UNDEAD | F_NON_LIVING | F_WAR_MACHINE)) !== 0) return;
  if (!rollChanceBp(c, "battle.morale", f.wake.chanceBp)) return;
  raiseWake(c, content, attacker, target, f.wake.creature);
}

function sideHasSource(s: BattleState, side: 0 | 1, source: ModifierSrcId): boolean {
  const st = s.stacks;
  for (let i = 0; i < st.n; i++) {
    if (st.side[i] !== side) continue;
    if (hasModFrom(s, i, source)) return true;
  }
  return false;
}

function raiseWake(
  c: Ctx, content: ContentRegistry, attacker: number, target: number, creature: CreatureDefId,
): void {
  const st = c.s.stacks;
  const side = st.side[attacker]! as 0 | 1;
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i) || st.side[i] !== side) continue;
    if (st.def[i] !== creature) continue;
    healStack(c, i, must(content.creatures, creature, "creature").hp, st.count[i]! + 1);
    return;
  }
  const twoHex = (flagsFor(content, creature) & F_TWO_HEX) !== 0;
  const hex = freeHexFor(c.s, side, st.hex[target]!, twoHex);
  addStack(c, content, { def: creature, side, count: 1, hex });
}

function flagsFor(content: ContentRegistry, id: CreatureDefId): number {
  const def = content.creatures.get(id);
  if (def === undefined) return 0;
  let f = 0;
  for (const a of def.abilities) if (a.kind === "TWO_HEX") f |= F_TWO_HEX;
  return f;
}

